import {Component, Input, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {EditorModelService} from '@irb/irb-editors/editor-model/editor-model.service';
import {IrbSettingsService} from '@irb/irb-settings/irb-settings.service';
import {AppService} from '@irb/app.service';
import {SecurityMasterAssetCategoryService} from '@irb/irb-models/security-master/security-master.asset-category.service';
import {DropdownSearchMultipleRendererComponent} from '@irb-common/components/grid/renderers/dropdown-search-multiple.cell.component';

@Component({ standalone: false,
    selector: 'irb-mmc-advisor-security-allocations',
    templateUrl: './mmc-advisor-security-allocations.component.html',
    styleUrls: ['./mmc-advisor-security-allocations.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class MmcAdvisorSecurityAllocationsComponent implements OnInit {

    @Input() securityList: any;

    grid: any;
    gridOptions: any;
    frameworkComponents: any;
    height: string = '100px';
    securities: any;
    isUiTest = false;
    private ENABLE_SEC_GRID_CHART_MMC: boolean = false;
    private allUncategorized: boolean = false;

    constructor(
        private editorModelService: EditorModelService,
        public iss: IrbSettingsService,
        private appService: AppService,
        private smac: SecurityMasterAssetCategoryService
    ) {
        this.ENABLE_SEC_GRID_CHART_MMC = this.appService.getItem('IRB_APP_CONFIG')?.featureFlags?.ENABLE_SEC_GRID_CHART_MMC || false;
        this.frameworkComponents = {
            DropdownSearchMultipleRendererComponent: DropdownSearchMultipleRendererComponent
        };
    }

    ngOnInit(): void {
        this.checkAllUncategorized();
        this.gridOptions = {
            columnDefs: this.getColumns(),
            floatingFilter: false
        };
        this.securities = this.securityList ? this.securityList : [];
        this.prepareDataForChart();
        this.setGridheight();

    }

    onGridReady(params: any) {
        this.grid = params;
        this.grid.api.setGridOption('rowData', this.securities);
        this.setGridheight();
        this.checkCategoriesColors();
    }

    private prepareDataForChart() {
        if (this.securityList && this.securityList.length !== 0) {
            this.securityList.forEach((sec: any) => {
                sec.customCategoryName = sec.custCatName;
                sec.targetPercentage = sec.target;
                sec.securitySymbol = sec.symbol;
            });
            this.editorModelService.createCustCatMap(this.securityList);
            this.editorModelService.changedData();
            this.checkAllUncategorized();
            this.enrichWithAssetClassData();
        }
    }

    private checkAllUncategorized() {
        if (!this.ENABLE_SEC_GRID_CHART_MMC) { return; }
        this.allUncategorized = this.securityList.every((sec: any) =>
            !sec.custCatName || sec.custCatName === 'Uncategorized'
        );
    }

    private enrichWithAssetClassData() {
        if (!this.ENABLE_SEC_GRID_CHART_MMC) { return; }

        const hasAcData = this.securityList.some((sec: any) =>
            sec.acLevel1 && sec.acLevel1.length > 0
        );

        if (hasAcData) {
            if (this.grid && this.grid.api) {
                this.grid.api.setGridOption('rowData', [...this.securities]);
            }
            this.editorModelService.createCustCatMap(this.securityList);
            this.editorModelService.changedData();
        }
    }

    private checkCategoriesColors() {
        if (this.securityList && this.securityList.length !== 0) {
            let rowIdx: number, mapColor: string;
            if (this.grid && this.grid.api) {
                this.grid.api.forEachNode((node: any) => {
                    rowIdx = node.rowIndex;
                    mapColor = this.editorModelService.getColorByCategory(node.data.customCategoryName ? node.data.customCategoryName : 'Uncategorized');
                    if (mapColor !== node.data.categoryColor) {
                        node.data.categoryColor = mapColor;
                        this.reDrawByIndex(rowIdx);
                    }
                });
            }
        }
    }

    private reDrawByIndex(rowIndex: number) {
        const n = this.grid.api.getDisplayedRowAtIndex(rowIndex);
        this.grid.api.redrawRows({rowNodes: [n]});
    }

    private getColumns() {
        const columns: any = [
            {
                headerName: 'Symbol',
                field: 'symbol',
                width: 150,
                minWidth: 100,
                suppressMovable: true
            },
            {
                headerName: 'Description',
                field: 'description',
                minWidth: 200,
                suppressMovable: true,
                cellRenderer: 'cellRendererFormatter'
            }
        ];
        if (!(this.ENABLE_SEC_GRID_CHART_MMC && this.allUncategorized)) {
            columns.push({
                headerName: 'Asset Category',
                field: 'custCatDesc',
                minWidth: 220,
                cellRenderer: 'colorCategoryComponent'
            });
        }
        if (this.ENABLE_SEC_GRID_CHART_MMC) {
            columns.push({
                headerName: 'Asset Class',
                field: 'acLevel1',
                minWidth: 160,
                flex: 1,
                suppressFiltersToolPanel: true,
                sortable: false,
                cellClass: 'dropdownSearchMultipleCell',
                cellRenderer: 'DropdownSearchMultipleRendererComponent',
                cellRendererParams: (params: any) => {
                    return {
                        defaultValue: this.smac.getCellDisplayForAssetClasses(params.data.acLevel1),
                        noneDisplayText: (params.data?.securitySymbol === '$CASH' || params.data?.symbol === '$CASH') ? 'Cash' : 'none',
                        cellStyle: 'classes',
                        canEdit: false,
                        hiddenLabelPanelTitle: 'Asset Classes',
                        hiddenLabelDescription: 'classes'
                    };
                }
            });
        }
        return columns;
    }

    private setGridheight() {
        const secData: any = this.securityList ? this.securityList.length : 0;
        let ht: number = secData * 30 + 33;
        ht = ht > 300 ? 300 : ht;
        if (secData < 2) {
            ht = 100;
        }
        if (this.grid && this.grid.api) {
            if (ht < 300)
                this.grid.api.setGridOption('domLayout', 'autoHeight');
            else
                this.grid.api.setGridOption('domLayout', 'normal');
        }
        this.height = ht + 'px';
    }
}

